import { Box, Center, Grid, Group, Loader, Paper, Stack, Text } from '@mantine/core'
import Image from 'next/image'
import { useRouter } from 'next/router'

import * as DisplayData from '@/components/commons/DisplayData'
import { useFetch } from '@/lib/hooks'
import {
  BestPaymentMethod as BestPaymentMethodType,
  PaymentMethodOption as PaymentMethodOptionType,
  Price as PriceType
} from '@/types'

import Conditions from './Conditions'

interface Props {
  type: string
  productSlug: string
}

interface PaymentMethodsData {
  price: PriceType
  bestPaymentMethods: BestPaymentMethodType[]
  availablePaymentMethods: PaymentMethodOptionType[]
}

export default function Available({ type, productSlug }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const {
    site: siteSlug,
    channel: channelSlug,
    showcase: showcaseSlug,
    store: storeSlug
  } = router.query || {}

  // Fetch
  const { data, error } = useFetch([
    `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/showcases/${showcaseSlug}/products/${productSlug}/payment-methods/`
  ])

  const paymentMethodsData: PaymentMethodsData = data || []
  const bestPaymentMethods: any = paymentMethodsData?.bestPaymentMethods
  const availablePaymentMethods: any = paymentMethodsData?.availablePaymentMethods

  if (!data && !error) {
    return (
      <Center mt="xl">
        <Loader variant="dots" />
      </Center>
    )
  }

  if (!availablePaymentMethods || !bestPaymentMethods) return null

  const paymentMethods = Object.keys(availablePaymentMethods)

  if (paymentMethods.length === 0) return null

  // Actions
  const renderPaymentMethods = (key: string, bestPaymentMethodType: BestPaymentMethodType) => {
    const { discountAmount, installmentAmount, interestAmount, interestRate, total, installments } =
      bestPaymentMethodType
    const totalPrice = <DisplayData.CurrencyField value={total} />
    const renderDiscountAmount =
      discountAmount > 0 ? (
        <Text color="green" size="xs">
          Desconto de <DisplayData.CurrencyField value={discountAmount} />
        </Text>
      ) : null
    const renderInterest =
      interestRate && interestRate > 0 && interestAmount && interestAmount > 0 ? (
        <>
          com juros de <DisplayData.PercentageField value={interestRate} /> a.m.
        </>
      ) : (
        'sem juros'
      )
    if (type === 'order') {
      switch (key) {
        case 'pix':
          return (
            <>
              <Text>
                {totalPrice} {'à vista no Pix'}
              </Text>
              {renderDiscountAmount}
            </>
          )
        case 'bankDebit':
          return (
            <>
              <Text>
                {totalPrice} {'à vista no débito em conta'}
              </Text>
              {renderDiscountAmount}
            </>
          )
        case 'billingNote':
          return (
            <>
              <Text>
                {totalPrice} {'à vista no boleto bancário'}
              </Text>
              {renderDiscountAmount}
            </>
          )
        case 'installmentBillingNote':
          return (
            <>
              <Text>{'Boleto parcelado. Sujeito a cobrança de juros.'}</Text>
              {renderDiscountAmount}
            </>
          )
        case 'creditCard':
          return (
            <>
              <Text>
                {totalPrice} {`em ${installments}x de `}{' '}
                <DisplayData.CurrencyField value={installmentAmount} /> {'no cartão de crédito'}{' '}
                {renderInterest}
              </Text>
              {renderDiscountAmount}
            </>
          )
        default:
          return ''
      }
    } else {
      switch (key) {
        case 'pix':
          return <>Pix</>
        case 'bankDebit':
          return <>Débito em conta</>
        case 'billingNote':
          return <>Boleto bancário</>
        case 'installmentBillingNote':
          return <>Boleto parcelado</>
        case 'creditCard':
          return <>Cartão de crédito</>
        default:
          return ''
      }
    }
  }

  return (
    <>
      <Paper p="sm" mt="sm" withBorder>
        <Stack spacing="sm">
          <Text size="sm" mb={0} weight="bold">
            Você poderá pagar utilizando:
          </Text>

          {paymentMethods
            ?.sort((keyA, keyB) => {
              if (keyA === 'installmentBillingNote') return 1
              if (keyB === 'installmentBillingNote') return -1
              return 0
            })
            .map((key: string) => (
              <Grid gutter={0} key={key}>
                <Grid.Col span={8}>
                  <Text size="sm">{renderPaymentMethods(key, bestPaymentMethods[key])}</Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Group spacing={2} position="right">
                    {availablePaymentMethods[key]?.map((option: PaymentMethodOptionType) => (
                      <Image
                        key={option.paymentMethod?.code}
                        alt={option.paymentMethod?.name}
                        width={20}
                        height={20}
                        src={option.paymentMethod?.brand?.logo}
                        style={{ borderRadius: '3px' }}
                      />
                    ))}
                  </Group>
                </Grid.Col>
              </Grid>
            ))}
        </Stack>
      </Paper>

      <Box hidden={type !== 'order'}>
        <Conditions
          paymentMethods={paymentMethods}
          availablePaymentMethods={availablePaymentMethods}
        />
      </Box>
    </>
  )
}
